import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
import 'dayjs/locale/pt-br.js';
import { scheduleService } from '../services/scheduleService.js';
import { medicalRecordService } from '../services/medicalRecordService.js';
import { documentService } from '../services/documentService.js';
import { formatPhone, getSpeciesLabel } from '../utils/formatters.js';
import toast from 'react-hot-toast';
import placeholderImage from '../images/placeholder.png';
import ScheduleDetailsModal from './ScheduleDetailsModal.js';
import ConfirmModal from './ConfirmModal.js';
import './FinalizeAppointment.css';
import './Dashboard.css';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('pt-br');

const EXAM_OPTIONS = [
    { id: 'hemograma', label: 'Hemograma Completo' },
    { id: 'bioquimico', label: 'Bioquímico' },
    { id: 'raio_x', label: 'Raio-X' },
    { id: 'ultrassom', label: 'Ultrassom' },
    { id: 'urinanalise', label: 'Urinálise' },
    { id: 'parasitologico', label: 'Parasitológico' },
];

const GENERAL_CONDITION_OPTIONS = [
    { value: 'good', label: 'Bom' },
    { value: 'fair', label: 'Regular' },
    { value: 'poor', label: 'Ruim' },
    { value: 'critical', label: 'Crítico' },
];

const HYDRATION_OPTIONS = [
    { value: 'normal', label: 'Normal' },
    { value: 'mild', label: 'Desidratação Leve (< 5%)' },
    { value: 'moderate', label: 'Desidratação Moderada (5-10%)' },
    { value: 'severe', label: 'Desidratação Grave (> 10%)' },
];

const NEEDS_RETURN_OPTIONS = [
    { value: 'yes', label: 'Sim' },
    { value: 'no', label: 'Não' },
    { value: 'if_needed', label: 'Se necessário' },
];

const PRIORITY_OPTIONS = [
    { value: 'low', label: 'Baixa' },
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'Alta' },
    { value: 'urgent', label: 'Urgente' },
];

const EMPTY_MEDICATION = {
    name: '', dosage: '',
    frequencyTimes: '', frequencyType: '',
    durationTimes: '', durationType: '',
    startDate: '', endDate: '',
    reminder: false, notes: ''
};

const FREQUENCY_TIMES_OPTIONS = [
    { value: 'once', label: '1 vez' },
    { value: 'twice', label: '2 vezes' },
    { value: 'three_times', label: '3 vezes' },
    { value: 'four_times', label: '4 vezes' },
];

const FREQUENCY_TYPE_OPTIONS = [
    { value: 'by_day', label: 'ao dia' },
    { value: 'by_month', label: 'ao mês' },
    { value: 'by_year', label: 'ao ano' },
];

const DURATION_TYPE_OPTIONS = [
    { value: 'days', label: 'dias' },
    { value: 'weeks', label: 'semanas' },
    { value: 'months', label: 'meses' },
];

const EMPTY_FORM = {
    weight: '',
    temperature: '',
    heartRate: '',
    respiratoryRate: '',
    generalCondition: '',
    hydrationLevel: '',
    physicalExamNotes: '',
    chiefComplaint: '',
    historyAndObservations: '',
    clinicalDiagnosis: '',
    requestedExams: [],
    otherExams: '',
    generalGuidelines: '',
    needsReturn: '',
    returnDeadline: '',
    returnPriority: '',
    returnNotes: '',
    internalNotes: '',
};

function FinalizeAppointment() {
    const { scheduleId } = useParams();
    const navigate = useNavigate();

    const [schedule, setSchedule] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [mode, setMode] = useState('form'); // 'form' | 'overview'
    const [isFinalized, setIsFinalized] = useState(false);
    const [savedData, setSavedData] = useState(null);
    const [consultationId, setConsultationId] = useState(null);

    const [medications, setMedications] = useState([]);
    const [medForm, setMedForm] = useState({ ...EMPTY_MEDICATION });
    const [editingMedIndex, setEditingMedIndex] = useState(null);
    const [showMedForm, setShowMedForm] = useState(false);
    const [savingMed, setSavingMed] = useState(false);

    // Documents
    const [documents, setDocuments] = useState([]);
    const [pendingDocFile, setPendingDocFile] = useState(null); // { file, previewUrl, fileName }
    const [pendingDocTitle, setPendingDocTitle] = useState('');
    const [pendingDocVisibleToTutor, setPendingDocVisibleToTutor] = useState(true);
    const [savingDoc, setSavingDoc] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const docInputRef = React.useRef(null);

    const [formData, setFormData] = useState({ ...EMPTY_FORM });
    const [hoveredItem, setHoveredItem] = useState(null);
    const [showEarlyFinalizeModal, setShowEarlyFinalizeModal] = useState(false);

    useEffect(() => {
        if (scheduleId) {
            loadScheduleDetails();
        }
    }, [scheduleId]);

    const loadScheduleDetails = async () => {
        setLoading(true);
        const result = await scheduleService.getScheduleDetails(scheduleId);
        if (result.success) {
            const s = result.schedule;
            if (!s) {
                toast.error('Agendamento não encontrado');
                navigate(-1);
                setLoading(false);
                return;
            }
            setSchedule(s);
            if (s.documents?.length) {
                setDocuments(s.documents.filter((d) => d.active !== false));
            }
            const c = s.consultation;
            if (c?.id) {
                setConsultationId(c.id);
                setMedications(c.medications?.filter((m) => m.active !== false) || []);
                const populated = {
                    ...EMPTY_FORM,
                    weight: c.weight || '',
                    temperature: c.temperature || '',
                    heartRate: c.heartRate || '',
                    respiratoryRate: c.respiratoryRate || '',
                    generalCondition: c.generalCondition || '',
                    hydrationLevel: c.hydrationLevel || '',
                    physicalExamNotes: c.physicalExamNotes || '',
                    chiefComplaint: c.chiefComplaint || '',
                    historyAndObservations: c.historyAndObservations || '',
                    clinicalDiagnosis: c.clinicalDiagnosis || '',
                    requestedExams: c.requestedExams || [],
                    otherExams: c.otherExams || '',
                    generalGuidelines: c.generalGuidelines || '',
                    needsReturn: c.needsReturn || '',
                    returnDeadline: c.returnDeadline || '',
                    returnPriority: c.returnPriority || '',
                    returnNotes: c.returnNotes || '',
                    internalNotes: c.internalNotes || '',
                };
                setFormData(populated);
                setSavedData(populated);
                if (s.status === 'done') {
                    setIsFinalized(true);
                }
                setMode('overview');
            }
        } else {
            toast.error(result.error || 'Erro ao carregar detalhes do agendamento');
            navigate(-1);
        }
        setLoading(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleExamToggle = (id) => {
        setFormData((prev) => {
            const exams = prev.requestedExams.includes(id)
                ? prev.requestedExams.filter((e) => e !== id)
                : [...prev.requestedExams, id];
            return { ...prev, requestedExams: exams };
        });
    };

    const handleMedFormChange = (field, value) => {
        setMedForm((prev) => {
            const updated = { ...prev, [field]: value };
            if (['startDate', 'durationTimes', 'durationType'].includes(field)) {
                const sd = field === 'startDate' ? value : updated.startDate;
                const dt = field === 'durationTimes' ? value : updated.durationTimes;
                const ty = field === 'durationType' ? value : updated.durationType;
                if (sd && dt && ty) {
                    const n = parseInt(dt, 10);
                    const unitMap = { days: 'day', weeks: 'week', months: 'month' };
                    updated.endDate = isNaN(n) ? '' : dayjs(sd).add(n, unitMap[ty] || 'day').format('YYYY-MM-DD');
                } else {
                    updated.endDate = '';
                }
            }
            return updated;
        });
    };

    const handleSaveMed = async () => {
        if (!medForm.name.trim()) { toast.error('Informe o nome do medicamento'); return; }
        if (!medForm.dosage.trim()) { toast.error('Informe a dosagem do medicamento'); return; }
        if (!medForm.frequencyTimes) { toast.error('Informe a frequência do medicamento'); return; }
        if (!medForm.frequencyType) { toast.error('Informe o tipo de frequência do medicamento'); return; }
        if (!medForm.startDate) { toast.error('Informe a data de início do medicamento'); return; }
        if (!medForm.durationTimes) { toast.error('Informe a duração do tratamento'); return; }
        if (!medForm.durationType) { toast.error('Informe o tipo de duração do tratamento'); return; }
        setSavingMed(true);
        const isEdit = editingMedIndex !== null;
        const existingMed = isEdit ? medications[editingMedIndex] : null;
        const medId = existingMed?.id || null;

        if (consultationId) {
            // Try v3 endpoint first; fall back to v2 scheduleService
            const isV3 = schedule?.consultation?.version === 'v3' || schedule?.source === 'v3';
            let result;
            if (isV3) {
                if (medId) {
                    result = await medicalRecordService.updateMedication(consultationId, medId, medForm);
                } else {
                    result = await medicalRecordService.saveMedication(consultationId, medForm);
                }
            } else {
                result = await scheduleService.saveMedication(schedule.pet?.id || null, consultationId, medForm, medId);
            }
            if (result.success) {
                if (isEdit) {
                    setMedications((prev) => prev.map((m, i) => i === editingMedIndex ? result.medication : m));
                } else {
                    setMedications((prev) => [...prev, result.medication]);
                }
                toast.success(isEdit ? 'Medicamento atualizado!' : 'Medicamento adicionado!');
                setShowMedForm(false);
                setMedForm({ ...EMPTY_MEDICATION });
                setEditingMedIndex(null);
            } else {
                toast.error(result.error || 'Erro ao salvar medicamento');
            }
        } else {
            if (isEdit) {
                setMedications((prev) => prev.map((m, i) => i === editingMedIndex ? { ...medForm } : m));
            } else {
                setMedications((prev) => [...prev, { ...medForm }]);
            }
            setShowMedForm(false);
            setMedForm({ ...EMPTY_MEDICATION });
            setEditingMedIndex(null);
        }
        setSavingMed(false);
    };

    const handleDeleteMed = async (index) => {
        const med = medications[index];
        if (med.id) {
            const result = await scheduleService.deleteMedication(med.id);
            if (result.success) {
                setMedications((prev) => prev.filter((_, i) => i !== index));
                toast.success('Medicamento removido!');
            } else {
                toast.error(result.error || 'Erro ao remover medicamento');
            }
        } else {
            setMedications((prev) => prev.filter((_, i) => i !== index));
        }
    };

    const handleEditMed = (index) => {
        const med = medications[index];
        setMedForm({
            name: med.name || '',
            dosage: med.dosage || '',
            frequencyTimes: med.frequencyTimes || '',
            frequencyType: med.frequencyType || '',
            durationTimes: String(med.durationTimes || ''),
            durationType: med.durationType || '',
            startDate: med.startDate || '',
            endDate: med.endDate || '',
            reminder: med.reminder || false,
            notes: med.notes || '',
        });
        setEditingMedIndex(index);
        setShowMedForm(true);
    };

    const openAddMedForm = () => {
        setMedForm({ ...EMPTY_MEDICATION });
        setEditingMedIndex(null);
        setShowMedForm(true);
    };

    const closeMedForm = () => {
        setShowMedForm(false);
        setEditingMedIndex(null);
        setMedForm({ ...EMPTY_MEDICATION });
    };

    /* ── Documents ─────────────────────────────────────────── */
    const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

    const handleDocFileSelect = (file) => {
        if (!file) return;
        if (!ACCEPTED_TYPES.includes(file.type)) {
            toast.error('Formato não suportado. Use JPG, PNG ou PDF.');
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            toast.error('Arquivo muito grande. Máximo 10MB.');
            return;
        }
        const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
        setPendingDocFile({ file, previewUrl, fileName: file.name });
        setPendingDocTitle(file.name.replace(/\.[^.]+$/, ''));
    };

    const handleDocDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file) handleDocFileSelect(file);
    };

    const handleDocConfirm = async () => {
        if (!pendingDocFile) return;
        if (!pendingDocTitle.trim()) { toast.error('Informe um título para o documento'); return; }
        setSavingDoc(true);
        setUploadProgress(0);

        const isV3 = schedule?.consultation?.version === 'v3' || schedule?.source === 'v3';

        if (isV3) {
            // v3: multipart/form-data upload
            const fd = new FormData();
            fd.append('file', pendingDocFile.file);
            if (schedule?.pet?.id) fd.append('petId', schedule.pet.id);
            if (consultationId) fd.append('consultationId', consultationId);
            fd.append('title', pendingDocTitle.trim());
            fd.append('type', documentService.getFileType(pendingDocFile.file));
            fd.append('visibleToTutor', pendingDocVisibleToTutor ? 'true' : 'false');
            const saveResult = await medicalRecordService.uploadDocument(fd);
            if (saveResult.success) {
                setDocuments((prev) => [...prev, saveResult.document]);
                toast.success('Documento adicionado!');
                setPendingDocFile(null);
                setPendingDocTitle('');
                setPendingDocVisibleToTutor(true);
            } else {
                toast.error(saveResult.error);
            }
            setSavingDoc(false);
            setUploadProgress(null);
            return;
        }

        const uploadResult = await documentService.uploadToFirebase(pendingDocFile.file, setUploadProgress);
        if (!uploadResult.success) {
            toast.error(uploadResult.error);
            setSavingDoc(false);
            setUploadProgress(null);
            return;
        }

        const saveResult = await documentService.saveDocument({
            ownerId: scheduleId || null,
            ownerType: 'Schedule',
            petId: schedule?.pet?.id,
            title: pendingDocTitle.trim(),
            fileUrl: uploadResult.fileUrl,
            fileName: uploadResult.fileName,
            fileType: pendingDocFile.file.type,
            visibleToTutor: pendingDocVisibleToTutor,
        });

        if (saveResult.success) {
            setDocuments((prev) => [...prev, saveResult.document]);
            toast.success('Documento adicionado!');
            setPendingDocFile(null);
            setPendingDocTitle('');
            setPendingDocVisibleToTutor(true);
        } else {
            toast.error(saveResult.error);
        }
        setSavingDoc(false);
        setUploadProgress(null);
    };

    const handleDocDelete = async (doc, index) => {
        if (!doc.id) {
            setDocuments((prev) => prev.filter((_, i) => i !== index));
            return;
        }
        const result = await documentService.deleteDocument(doc.id);
        if (result.success) {
            setDocuments((prev) => prev.filter((_, i) => i !== index));
            toast.success('Documento removido!');
        } else {
            toast.error(result.error);
        }
    };

    const getDocIcon = (doc) => {
        const name = (doc.fileName || doc.title || '').toLowerCase();
        if (name.endsWith('.pdf')) return 'pdf';
        if (/\.(jpg|jpeg|png|webp)$/.test(name)) return 'img';
        return 'doc';
    };

    const handleSaveDraft = async () => {
        setSubmitting(true);
        const result = await scheduleService.saveDraft(scheduleId, schedule.pet?.id, formData, consultationId, schedule.entityId);
        if (result.success) {
            const newConsultationId = result.consultation?.id || consultationId;
            if (newConsultationId) setConsultationId(newConsultationId);

            // Batch-save any medications added locally before the consultation existed
            if (newConsultationId) {
                const queued = medications.filter((m) => !m.id);
                if (queued.length > 0) {
                    const saved = [];
                    for (const med of queued) {
                        const mr = await scheduleService.saveMedication(schedule.pet?.id || null, newConsultationId, med);
                        if (mr.success) saved.push(mr.medication);
                    }
                    if (saved.length > 0) {
                        setMedications((prev) => [...prev.filter((m) => m.id), ...saved]);
                    }
                }
            }

            setSavedData({ ...formData });
            setMode('overview');
            toast.success('Rascunho salvo!');
        } else {
            toast.error(result.error || 'Erro ao salvar rascunho');
        }
        setSubmitting(false);
    };

    const doFinalize = async () => {
        // Usa `savedData` se estiver em modo 'overview', caso contrário, usa `formData` do formulário.
        const dataToFinalize = mode === 'overview' ? savedData : formData;
        setSubmitting(true);
        const result = await scheduleService.finalizeSchedule(scheduleId, schedule.pet?.id, dataToFinalize, consultationId, schedule.entityId);
        if (result.success) {
            const newConsultationId = result.consultation?.id;
            if (newConsultationId) setConsultationId(newConsultationId);

            // Batch-save any medications that were added locally before the consultation existed
            if (newConsultationId) {
                const queued = medications.filter((m) => !m.id);
                if (queued.length > 0) {
                    const saved = [];
                    for (const med of queued) { // Usar um loop `for...of` para garantir a execução sequencial com `await`
                        const mr = await scheduleService.saveMedication(schedule.pet?.id || null, newConsultationId, med);
                        if (mr.success) saved.push(mr.medication);
                    }
                    if (saved.length > 0) {
                        setMedications((prev) => [...prev.filter((m) => m.id), ...saved]);
                    }
                }
            }

            setSavedData({ ...dataToFinalize });
            setIsFinalized(true);
            setMode('overview');
            toast.success('Atendimento finalizado com sucesso!');
        } else {
            toast.error(result.error || 'Erro ao finalizar atendimento');
        }
        setSubmitting(false);
    };

    const handleFinalize = () => {
        const appointmentDate = dayjs.utc(schedule.dateTime).startOf('day');
        const today = dayjs().startOf('day');

        if (today.isBefore(appointmentDate)) {
            setShowEarlyFinalizeModal(true);
            return;
        }

        doFinalize();
    };

    const handleEdit = () => {
        setMode('form');
    };

    const handleCancel = () => {
        navigate(-1);
    };

    /* ── Helpers ─────────────────────────────────────────── */
    const formatDate = (dt) => dayjs.utc(dt).format('ddd., D [de] MMM [de] YYYY'); // Já estava correto
    const formatTime = (dt) => dayjs.utc(dt).format('HH:mm'); // Já estava correto

    const copyToClipboard = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => toast.success('Copiado!'));
    };

    const calculateAge = (birthday) => {
        if (!birthday) return '0 ano(s)';
        const years = dayjs().diff(dayjs(birthday), 'year');
        const months = dayjs().diff(dayjs(birthday), 'month') % 12;
        if (years === 0) return `${months} mês${months !== 1 ? 'es' : ''}`;
        if (months === 0) return `${years} ano${years !== 1 ? 's' : ''}`;
        return `${years} ano${years !== 1 ? 's' : ''} e ${months} mês${months !== 1 ? 'es' : ''}`;
    };

    const getSexLabel = (s) => (s === 'male' ? 'Macho' : s === 'female' ? 'Fêmea' : s);

    const getLabelFor = (options, value) =>
        options.find((o) => o.value === value)?.label || value;

    /* ── Loading / Error states ──────────────────────────── */
    if (loading) {
        return (
            <div className="fa-loading-screen">
                <div className="fa-spinner" />
            </div>
        );
    }
    if (!schedule) return null;

    /* ── Left sidebar (shared between modes) ────────────── */
    const sidebar = (
        <aside className="fa-summary-col">
            <section className="fa-card">
                <h2 className="fa-card-title">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                    </svg>
                    Tutor
                </h2>
                <div className="schedule-user-info">
                    {schedule.user?.image ? (
                        <img src={schedule.user.image} alt={schedule.user.name} className="schedule-user-avatar"
                            onError={(e) => { e.target.onerror = null; e.target.src = placeholderImage; }} />
                    ) : (
                        <div className="schedule-user-avatar-placeholder">{schedule.user?.name?.charAt(0)?.toUpperCase()}</div>
                    )}
                    <div className="schedule-user-details">
                        <h3>{schedule.user?.name}</h3>
                        <div className="schedule-user-contact-item">
                            <p
                                className="schedule-user-clickable"
                                onClick={() => copyToClipboard(schedule.user?.email)}
                                onMouseEnter={() => setHoveredItem('email')}
                                onMouseLeave={() => setHoveredItem(null)}
                            >
                                {schedule.user?.email}
                            </p>
                            {hoveredItem === 'email' && (
                                <div className="schedule-tooltip">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                    </svg>
                                    Clique para copiar
                                </div>
                            )}
                        </div>
                        <div className="schedule-user-contact-item">
                            <p
                                className="schedule-user-clickable"
                                onClick={() => copyToClipboard(schedule.user?.phone)}
                                onMouseEnter={() => setHoveredItem('phone')}
                                onMouseLeave={() => setHoveredItem(null)}
                            >
                                {formatPhone(schedule.user?.phone)}
                            </p>
                            {hoveredItem === 'phone' && (
                                <div className="schedule-tooltip">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                    </svg>
                                    Clique para copiar
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {schedule.pet && (
                <section className="fa-card">
                    <div className="fa-card-title-row">
                    <h2 className="fa-card-title">
                        <svg width="20" height="20" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g>
                                <path fill="currentColor" d="M120.113,139.014c4.529,13.248,13.07,24.156,23.738,31.637c10.672,7.491,23.51,11.665,36.848,11.673
    c6.896,0,13.906-1.126,20.726-3.458l0.023-0.008c13.24-4.528,24.144-13.066,31.626-23.738c7.49-10.664,11.668-23.502,11.672-36.844
    c0-6.891-1.126-13.917-3.469-20.753c-4.525-13.232-13.063-24.14-23.726-31.63c-10.668-7.49-23.502-11.672-36.848-11.672
    c-6.888,0-13.913,1.119-20.765,3.474c-13.236,4.528-24.14,13.066-31.614,23.731c-7.494,10.664-11.672,23.501-11.676,36.843
    c0,6.876,1.122,13.878,3.45,20.698L120.113,139.014z M154.716,99.964c3.749-5.308,9.042-9.475,15.689-11.751l-1.39,0.473
    l1.41-0.489c3.43-1.174,6.868-1.717,10.278-1.717c6.565-0.008,12.984,2.072,18.296,5.813c5.312,3.749,9.475,9.042,11.759,15.682
    v0.007c1.182,3.442,1.728,6.892,1.728,10.294c0.004,6.569-2.075,12.988-5.808,18.304c-3.749,5.317-9.042,9.475-15.689,11.759h0.023
    c-3.458,1.174-6.915,1.717-10.314,1.717c-6.572,0.008-12.991-2.071-18.304-5.804c-5.3-3.733-9.456-9.01-11.743-15.641l-0.016-0.04
    c-1.182-3.458-1.729-6.9-1.729-10.302C148.904,111.7,150.983,105.281,154.716,99.964z"/>
                                <path fill="currentColor" d="M120.109,139.014c-0.003-0.016-0.003-0.032-0.012-0.048l-0.422-1.228L120.109,139.014z" />
                                <path fill="currentColor" d="M312.969,175.244l-2.182-0.748l2.198,0.756c6.833,2.339,13.846,3.458,20.73,3.458
    c13.346-0.008,26.18-4.182,36.852-11.672c10.668-7.482,19.21-18.391,23.738-31.63c2.343-6.844,3.466-13.862,3.466-20.753
    c-0.004-13.342-4.182-26.18-11.676-36.851c-7.478-10.664-18.39-19.21-31.634-23.731l0.007,0.007
    c-6.828-2.347-13.849-3.481-20.753-3.481c-13.342,0.008-26.18,4.19-36.848,11.68c-10.664,7.482-19.205,18.39-23.726,31.638
    c-2.344,6.829-3.47,13.854-3.47,20.746c0.004,13.342,4.182,26.18,11.672,36.843C288.825,162.169,299.729,170.715,312.969,175.244z
     M303.659,104.367c2.284-6.655,6.446-11.948,11.759-15.697c5.312-3.734,11.727-5.813,18.296-5.813c3.39,0,6.84,0.552,10.294,1.732
    l0.008,0.008c6.648,2.276,11.936,6.435,15.686,11.751c3.733,5.316,5.812,11.735,5.808,18.304c0,3.403-0.548,6.852-1.725,10.302
    c-2.284,6.64-6.45,11.932-11.763,15.682c-5.312,3.734-11.736,5.812-18.308,5.812c-3.403,0-6.856-0.552-10.302-1.725l2.182,0.748
    l-2.166-0.74c-6.647-2.292-11.94-6.458-15.689-11.767c-3.733-5.317-5.812-11.736-5.808-18.304
    C301.931,111.259,302.478,107.809,303.659,104.367z"/>
                                <path fill="currentColor" d="M500.324,202.392c-7.478-10.664-18.391-19.209-31.634-23.73h0.008c-6.829-2.339-13.85-3.474-20.753-3.466
    c-13.342,0-26.18,4.174-36.848,11.672c-10.664,7.482-19.205,18.398-23.726,31.638c-2.343,6.829-3.469,13.854-3.469,20.754
    c0.003,13.334,4.182,26.172,11.672,36.836c7.483,10.672,18.387,19.209,31.626,23.738l0.016,0.008
    c6.832,2.332,13.846,3.458,20.73,3.458c13.346-0.008,26.18-4.182,36.852-11.673c10.668-7.482,19.209-18.39,23.738-31.638
    c2.343-6.836,3.466-13.854,3.466-20.746C511.996,225.902,507.818,213.064,500.324,202.392z M478.015,249.546
    c-2.284,6.639-6.45,11.932-11.763,15.681c-5.312,3.733-11.736,5.812-18.308,5.804c-3.403,0-6.856-0.543-10.302-1.717h0.016
    c-6.647-2.284-11.94-6.45-15.689-11.766c-3.733-5.308-5.812-11.727-5.808-18.288c0-3.403,0.547-6.852,1.728-10.302
    c2.284-6.655,6.446-11.948,11.759-15.697c5.312-3.732,11.727-5.812,18.296-5.804c3.39,0,6.84,0.544,10.293,1.725l0.008,0.008
    c6.647,2.276,11.936,6.442,15.685,11.751c3.733,5.308,5.812,11.736,5.809,18.304C479.74,242.646,479.192,246.096,478.015,249.546z"
                                />
                                <path fill="currentColor" d="M124.634,259.989c2.343-6.836,3.466-13.854,3.466-20.746c-0.004-13.342-4.183-26.18-11.677-36.852
    c-7.478-10.664-18.39-19.209-31.634-23.73l-0.024-0.008c-6.816-2.331-13.83-3.466-20.722-3.458
    c-13.342,0-26.18,4.174-36.848,11.672C16.532,194.35,7.99,205.266,3.469,218.506v-0.007C1.126,225.335,0,232.36,0,239.26
    c0.004,13.334,4.182,26.172,11.672,36.836c7.483,10.672,18.387,19.209,31.626,23.738l0.016,0.008
    c6.832,2.332,13.846,3.458,20.73,3.458c13.346-0.008,26.18-4.182,36.852-11.673C111.564,284.145,120.106,273.237,124.634,259.989z
     M94.114,249.546c-2.284,6.639-6.451,11.932-11.763,15.681c-5.312,3.733-11.735,5.812-18.308,5.804
    c-3.402,0-6.856-0.543-10.302-1.717h0.016c-6.647-2.284-11.94-6.45-15.689-11.766c-3.734-5.308-5.813-11.727-5.808-18.288
    c0-3.411,0.547-6.852,1.728-10.302l0.012-0.032c2.288-6.639,6.442-11.924,11.746-15.666c5.313-3.732,11.728-5.812,18.296-5.804
    c3.391,0,6.84,0.544,10.294,1.725l3.052,1.048l-3.044-1.04c6.647,2.276,11.936,6.442,15.686,11.751
    c3.732,5.308,5.812,11.736,5.808,18.304C95.839,242.646,95.292,246.096,94.114,249.546z"/>
                                <path fill="currentColor" d="M338.475,234.219c-12.318-9.83-25.66-17.973-39.687-23.754c-14.02-5.766-28.774-9.168-43.739-9.168
    c-19.966,0.016-39.502,6.017-57.526,15.712c-27.039,14.594-51.025,37.529-68.734,64.182c-17.626,26.66-29.134,57.18-29.204,87.755
    c0,11.105,1.568,22.194,5.04,32.898c3.666,11.318,8.585,20.981,14.674,29c9.097,12.066,20.891,20.115,33.122,24.636
    c12.259,4.56,24.829,5.915,36.864,5.922c13.24-0.007,25.975-1.654,37.364-3.174c11.349-1.544,21.458-2.915,28.401-2.882
    c6.938-0.032,17.051,1.339,28.397,2.882c11.393,1.52,24.128,3.166,37.368,3.174c15.988-0.015,33.17-2.402,48.867-11.633
    c7.801-4.599,15.047-10.908,21.119-18.925c6.088-8.018,11.007-17.682,14.674-28.992c3.473-10.711,5.04-21.8,5.04-32.905
    c-0.016-20.376-5.21-40.751-13.814-59.952C383.734,280.207,363.084,253.893,338.475,234.219z M374.788,391.897
    c-2.733,8.427-6.08,14.689-9.688,19.446c-5.446,7.096-11.42,11.137-18.718,13.917c-7.274,2.732-15.996,3.891-25.569,3.883
    c-10.51,0.008-21.899-1.378-33.076-2.882c-11.215-1.489-22.112-3.142-32.689-3.174c-10.577,0.031-21.474,1.685-32.689,3.165
    c-11.176,1.512-22.565,2.899-33.076,2.891c-12.818,0.016-23.896-2.056-32.457-7.152c-4.328-2.575-8.207-5.898-11.83-10.648
    c-3.607-4.757-6.954-11.018-9.688-19.446c-2.332-7.199-3.466-14.87-3.466-22.951c-0.015-14.791,3.879-30.929,10.987-46.737
    c10.605-23.714,28.436-46.484,48.906-62.772c10.223-8.159,21.072-14.712,31.846-19.138c10.782-4.442,21.438-6.75,31.465-6.742
    c13.354-0.016,27.893,4.127,42.239,11.861c21.517,11.53,42.258,31.094,57.136,53.596c14.96,22.478,23.899,47.76,23.829,69.931
    C378.254,377.027,377.119,384.698,374.788,391.897z"/>
                            </g>
                        </svg>
                        Pet
                    </h2>
                    <span className="ref-tag">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="4" r="2"/><circle cx="18" cy="8" r="2"/><circle cx="4" cy="8" r="2"/><ellipse cx="11" cy="16" rx="5" ry="4"/>
                        </svg>
                        Pet #{schedule.pet.id}
                    </span>
                </div>
                <div className="fa-pet-card">
                        {schedule.pet.photo && (
                            <img src={schedule.pet.photo} alt={schedule.pet.name} className="fa-pet-card-photo"
                                onError={(e) => { e.target.onerror = null; e.target.src = placeholderImage; }} />
                        )}
                        <div className="fa-pet-card-info">
                            <h4 className="fa-pet-card-name">{schedule.pet.name}</h4>
                            <p className="fa-pet-card-breed">
                                {schedule.pet.breed} • {getSexLabel(schedule.pet.sex)} • {calculateAge(schedule.pet.birthday)}
                            </p>
                            <div className="fa-pet-card-details">
                                <span className="fa-pet-card-detail-item">
                                    <svg width="14" height="14" viewBox="0 0 512 512" fill="currentColor">
                                        <path d="M226.5 92.9c14.3 42.9-.3 86.2-32.6 96.8s-70.1-15.6-84.4-58.5s.3-86.2 32.6-96.8s70.1 15.6 84.4 58.5zM100.4 198.6c18.9 32.4 14.3 70.1-10.2 84.1s-59.7-.9-78.5-33.3S-2.7 179.3 21.8 165.3s59.7 .9 78.5 33.3zM69.2 401.2C121.6 259.9 214.7 224 256 224s134.4 35.9 186.8 177.2c3.6 9.7 5.2 20.1 5.2 30.5v1.6c0 25.8-20.9 46.7-46.7 46.7c-11.5 0-22.9-1.4-34-4.2l-88-22c-15.3-3.8-31.3-3.8-46.6 0l-88 22c-11.1 2.8-22.5 4.2-34 4.2C84.9 480 64 459.1 64 433.3v-1.6c0-10.4 1.6-20.8 5.2-30.5zM421.8 282.7c-24.5-14-29.1-51.7-10.2-84.1s54-47.3 78.5-33.3s29.1 51.7 10.2 84.1s-54 47.3-78.5 33.3zM310.1 189.7c-32.3-10.6-46.9-53.9-32.6-96.8s52.1-69.1 84.4-58.5s46.9 53.9 32.6 96.8s-52.1 69.1-84.4 58.5z" />
                                    </svg>
                                    {getSpeciesLabel(schedule.pet.species)}
                                </span>
                                {schedule.pet.weight && (
                                    <span className="fa-pet-card-detail-item">
                                        <svg width="14" height="14" viewBox="0 0 56 50" fill="currentColor">
                                            <circle cx="28" cy="9" r="6" fill="none" stroke="currentColor" strokeWidth="3" />
                                            <path d="M20 15 L14 19 L8 50 L48 50 L42 19 L36 15" fill="currentColor" />
                                            <text x="28" y="38" fontSize="16" fontWeight="bold" fill="white" textAnchor="middle">KG</text>
                                        </svg>
                                        {schedule.pet.weight} kg
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            <section className="fa-card">
                <div className="fa-card-title-row">
                    <h2 className="fa-card-title">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        Detalhes do Agendamento
                    </h2>
                    <span className="ref-tag">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                        Agend. #{scheduleId}
                    </span>
                </div>
                <button className="fa-btn-details" onClick={() => setShowDetailsModal(true)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    Ver detalhes do agendamento
                </button>
            </section>
        </aside>
    );

    /* ══════════════════════════════════════════════════════
       FORM MODE
    ══════════════════════════════════════════════════════ */
    if (mode === 'form') {
        return (
            <div className="fa-page">
                <header className="fa-header">
                    <button className="fa-back-btn" onClick={handleCancel} aria-label="Voltar">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                        Voltar
                    </button>
                    <div className="fa-header-center">
                        <h1 className="fa-page-title">Finalizar Atendimento</h1>
                        <span className="fa-appointment-date">
                            {formatDate(schedule.dateTime)} · {formatTime(schedule.dateTime)}
                        </span>
                    </div>
                    <div className="fa-header-spacer" />
                </header>

                <div className="fa-body">
                    {sidebar}

                    <main className="fa-form-col">

                        {/* ── Exame Clínico ── */}
                        <section className="fa-card fa-section-card">
                            <div className="fa-section-header">
                                <span className="fa-section-icon fa-section-icon--teal">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                                    </svg>
                                </span>
                                <h2 className="fa-section-title">Exame Clínico</h2>
                            </div>
                            <div className="fa-grid-4">
                                <div className="fa-field">
                                    <label className="fa-label">Peso (kg)</label>
                                    <input type="number" name="weight" className="fa-input" placeholder="32.5"
                                        value={formData.weight} onChange={handleChange} step="0.1" min="0" />
                                </div>
                                <div className="fa-field">
                                    <label className="fa-label">Temperatura (°C)</label>
                                    <input type="number" name="temperature" className="fa-input" placeholder="38.5"
                                        value={formData.temperature} onChange={handleChange} step="0.1" />
                                </div>
                                <div className="fa-field">
                                    <label className="fa-label">Freq. Cardíaca</label>
                                    <input type="number" name="heartRate" className="fa-input" placeholder="90"
                                        value={formData.heartRate} onChange={handleChange} min="0" />
                                </div>
                                <div className="fa-field">
                                    <label className="fa-label">Freq. Respiratória</label>
                                    <input type="number" name="respiratoryRate" className="fa-input" placeholder="28"
                                        value={formData.respiratoryRate} onChange={handleChange} min="0" />
                                </div>
                            </div>
                            <div className="fa-grid-2">
                                <div className="fa-field">
                                    <label className="fa-label">Estado Geral</label>
                                    <select name="generalCondition" className="fa-select"
                                        value={formData.generalCondition} onChange={handleChange}>
                                        <option value="">Selecione...</option>
                                        {GENERAL_CONDITION_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="fa-field">
                                    <label className="fa-label">Nível de Hidratação</label>
                                    <select name="hydrationLevel" className="fa-select"
                                        value={formData.hydrationLevel} onChange={handleChange}>
                                        <option value="">Selecione...</option>
                                        {HYDRATION_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="fa-field" style={{ marginTop: '16px' }}>
                                <label className="fa-label">Observações</label>
                                <textarea name="physicalExamNotes" className="fa-textarea" rows={4}
                                    placeholder="Adicione observações relevantes do exame físico"
                                    value={formData.physicalExamNotes} onChange={handleChange} />
                            </div>
                        </section>

                        {/* ── Anamnese ── */}
                        <section className="fa-card fa-section-card">
                            <div className="fa-section-header">
                                <span className="fa-section-icon fa-section-icon--blue">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                    </svg>
                                </span>
                                <h2 className="fa-section-title">Anamnese</h2>
                            </div>
                            <div className="fa-field">
                                <label className="fa-label">Queixa Principal / Motivo da Consulta</label>
                                <textarea name="chiefComplaint" className="fa-textarea" rows={4}
                                    placeholder="Descreva o motivo da consulta e queixas do tutor..."
                                    value={formData.chiefComplaint} onChange={handleChange} />
                            </div>
                            <div className="fa-field" style={{ marginTop: '16px' }}>
                                <label className="fa-label">Histórico e Observações</label>
                                <textarea name="historyAndObservations" className="fa-textarea" rows={4}
                                    placeholder="Histórico médico relevante, comportamento, alimentação, etc..."
                                    value={formData.historyAndObservations} onChange={handleChange} />
                            </div>
                        </section>

                        {/* ── Diagnóstico e Avaliação ── */}
                        <section className="fa-card fa-section-card">
                            <div className="fa-section-header">
                                <span className="fa-section-icon fa-section-icon--purple">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                                    </svg>
                                </span>
                                <h2 className="fa-section-title">Diagnóstico e Avaliação</h2>
                            </div>
                            <div className="fa-field">
                                <label className="fa-label">Diagnóstico Clínico</label>
                                <textarea name="clinicalDiagnosis" className="fa-textarea" rows={4}
                                    placeholder="Descreva o diagnóstico ou suspeita clínica..."
                                    value={formData.clinicalDiagnosis} onChange={handleChange} />
                            </div>
                            <div className="fa-exams-row" style={{ marginTop: '16px' }}>
                                <div className="fa-field">
                                    <label className="fa-label">Exames Solicitados</label>
                                    <div className="fa-checkbox-list">
                                        {EXAM_OPTIONS.map((exam) => (
                                            <label key={exam.id} className="fa-checkbox-label">
                                                <input type="checkbox" className="fa-checkbox"
                                                    checked={formData.requestedExams.includes(exam.id)}
                                                    onChange={() => handleExamToggle(exam.id)} />
                                                {exam.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="fa-field">
                                    <label className="fa-label">Outros Exames</label>
                                    <textarea name="otherExams" className="fa-textarea" rows={6}
                                        placeholder="Especifique outros exames..."
                                        value={formData.otherExams} onChange={handleChange} />
                                </div>
                            </div>
                        </section>

                        {/* ── Tratamento e Prescrições ── */}
                        <section className="fa-card fa-section-card">
                            <div className="fa-section-header">
                                <span className="fa-section-icon fa-section-icon--orange">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                                    </svg>
                                </span>
                                <h2 className="fa-section-title">Tratamento e Prescrições</h2>
                                {!showMedForm && (
                                    <button type="button" className="fa-btn-add-med" onClick={openAddMedForm}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                            <line x1="12" y1="5" x2="12" y2="19" />
                                            <line x1="5" y1="12" x2="19" y2="12" />
                                        </svg>
                                        Adicionar Medicamento
                                    </button>
                                )}
                            </div>

                            {/* list */}
                            {medications.length > 0 && (
                                <div className="fa-med-list">
                                    {medications.map((med, i) => (
                                        <div key={med.id || i} className="fa-med-list-item">
                                            <div className="fa-med-list-item-header">
                                                <div className="fa-med-list-item-info">
                                                    <p className="fa-med-list-item-name">{med.name}</p>
                                                    <div className="fa-med-list-item-details">
                                                        {med.dosage && <span>{med.dosage}</span>}
                                                        {med.frequencyTimes && med.frequencyType && (
                                                            <span>
                                                                {FREQUENCY_TIMES_OPTIONS.find((o) => o.value === med.frequencyTimes)?.label || med.frequencyTimes}{' '}
                                                                {FREQUENCY_TYPE_OPTIONS.find((o) => o.value === med.frequencyType)?.label || med.frequencyType}
                                                            </span>
                                                        )}
                                                        {med.durationTimes && med.durationType && (
                                                            <span>{med.durationTimes} {DURATION_TYPE_OPTIONS.find((o) => o.value === med.durationType)?.label || med.durationType}</span>
                                                        )}
                                                        {med.startDate && <span>{dayjs(med.startDate).format('DD/MM/YYYY')}</span>}
                                                    </div>
                                                    {med.notes && <p className="fa-med-list-item-notes">{med.notes}</p>}
                                                </div>
                                                <div className="fa-med-list-item-actions">
                                                    <button type="button" className="fa-med-action-btn fa-med-action-btn--edit"
                                                        onClick={() => handleEditMed(i)} aria-label="Editar medicamento">
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                        </svg>
                                                    </button>
                                                    <button type="button" className="fa-med-action-btn fa-med-action-btn--delete"
                                                        onClick={() => handleDeleteMed(i)} aria-label="Remover medicamento">
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                            <polyline points="3 6 5 6 21 6" />
                                                            <path d="M19 6l-1 14H6L5 6" />
                                                            <path d="M10 11v6M14 11v6" />
                                                            <path d="M9 6V4h6v2" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* add / edit form panel */}
                            {showMedForm && (
                                <div className="fa-med-form-panel">
                                    <h4 className="fa-med-form-title">{editingMedIndex !== null ? 'Editar Medicamento' : 'Novo Medicamento'}</h4>
                                    <div className="fa-grid-2">
                                        <div className="fa-field">
                                            <label className="fa-label">Nome do Medicamento *</label>
                                            <input type="text" className="fa-input" placeholder="Ex.: Amoxicilina" required
                                                value={medForm.name} onChange={(e) => handleMedFormChange('name', e.target.value)} />
                                        </div>
                                        <div className="fa-field">
                                            <label className="fa-label">Dosagem *</label>
                                            <input type="text" className="fa-input" placeholder="Ex.: 250mg ou 5mg/kg" required
                                                value={medForm.dosage} onChange={(e) => handleMedFormChange('dosage', e.target.value)} />
                                        </div>
                                    </div>
                                    <div className="fa-field">
                                        <label className="fa-label">Frequência *</label>
                                        <div className="fa-med-row">
                                            <select className="fa-select" value={medForm.frequencyTimes} required
                                                onChange={(e) => handleMedFormChange('frequencyTimes', e.target.value)}>
                                                <option value="">Selecione...</option>
                                                {FREQUENCY_TIMES_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                            </select>
                                            <select className="fa-select" value={medForm.frequencyType} required
                                                onChange={(e) => handleMedFormChange('frequencyType', e.target.value)}>
                                                <option value="">Selecione...</option>
                                                {FREQUENCY_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="fa-grid-2">
                                        <div className="fa-field">
                                            <label className="fa-label">Data de Início *</label>
                                            <input type="date" className="fa-input" required
                                                value={medForm.startDate} onChange={(e) => handleMedFormChange('startDate', e.target.value)} />
                                        </div>
                                        <div className="fa-field">
                                            <label className="fa-label">Duração <span style={{ color: 'red' }}>*</span></label>
                                            <div className="fa-med-row">
                                                <input type="number" className="fa-input" placeholder="Ex.: 7" min="1" required
                                                    value={medForm.durationTimes} onChange={(e) => handleMedFormChange('durationTimes', e.target.value)} />
                                                <select className="fa-select" value={medForm.durationType} required
                                                    onChange={(e) => handleMedFormChange('durationType', e.target.value)}>
                                                    <option value="">Selecione...</option>
                                                    {DURATION_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="fa-field">
                                        <label className="fa-label">Data de Término (calculada)</label>
                                        <input type="date" className="fa-input fa-input--readonly"
                                            value={medForm.endDate} readOnly />
                                    </div>
                                    <div className="fa-field">
                                        <label className="fa-label">Notas / Instruções de Uso</label>
                                        <textarea className="fa-textarea" rows={2} placeholder="Ex.: Administrar após alimentação"
                                            value={medForm.notes} onChange={(e) => handleMedFormChange('notes', e.target.value)} />
                                    </div>
                                    {/* <div className="fa-med-reminder-row">
                    <label className="fa-checkbox-label">
                      <input type="checkbox" className="fa-checkbox"
                        checked={medForm.reminder} onChange={(e) => handleMedFormChange('reminder', e.target.checked)} />
                      Ativar lembrete
                    </label>
                  </div> */}
                                    <div className="fa-med-form-actions">
                                        <button type="button" className="fa-btn-med-cancel" onClick={closeMedForm}>Cancelar</button>
                                        <button type="button" className="fa-btn-med-save" onClick={handleSaveMed} disabled={savingMed}>
                                            {savingMed ? 'Salvando...' : editingMedIndex !== null ? 'Salvar Alterações' : 'Adicionar'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="fa-field" style={{ marginTop: '16px' }}>
                                <label className="fa-label">Orientações Gerais ao Tutor</label>
                                <textarea name="generalGuidelines" className="fa-textarea" rows={4}
                                    placeholder="Recomendações de cuidados, restrições alimentares, atividades, etc..."
                                    value={formData.generalGuidelines} onChange={handleChange} />
                            </div>
                        </section>

                        {/* ── Retorno e Acompanhamento ── */}
                        <section className="fa-card fa-section-card">
                            <div className="fa-section-header">
                                <span className="fa-section-icon fa-section-icon--green">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                        <line x1="16" y1="2" x2="16" y2="6" />
                                        <line x1="8" y1="2" x2="8" y2="6" />
                                        <line x1="3" y1="10" x2="21" y2="10" />
                                    </svg>
                                </span>
                                <h2 className="fa-section-title">Retorno e Acompanhamento</h2>
                            </div>
                            <div className="fa-grid-3">
                                <div className="fa-field">
                                    <label className="fa-label">Necessita Retorno?</label>
                                    <select name="needsReturn" className="fa-select"
                                        value={formData.needsReturn} onChange={handleChange}>
                                        <option value="">Selecione...</option>
                                        {NEEDS_RETURN_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="fa-field">
                                    <label className="fa-label">Prazo para Retorno</label>
                                    <input type="text" name="returnDeadline" className="fa-input" placeholder="Ex: 7 dias"
                                        value={formData.returnDeadline} onChange={handleChange} />
                                </div>
                                <div className="fa-field">
                                    <label className="fa-label">Prioridade</label>
                                    <select name="returnPriority" className="fa-select"
                                        value={formData.returnPriority} onChange={handleChange}>
                                        <option value="">Selecione...</option>
                                        {PRIORITY_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="fa-field">
                                <label className="fa-label">Observações para Próxima Consulta</label>
                                <textarea name="returnNotes" className="fa-textarea" rows={3}
                                    placeholder="Pontos importantes para acompanhar no retorno..."
                                    value={formData.returnNotes} onChange={handleChange} />
                            </div>
                        </section>

                        {/* ── Anotações Internas ── */}
                        <section className="fa-card fa-section-card">
                            <div className="fa-section-header">
                                <span className="fa-section-icon fa-section-icon--gray">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                        <line x1="3" y1="9" x2="21" y2="9" />
                                        <line x1="9" y1="21" x2="9" y2="9" />
                                    </svg>
                                </span>
                                <h2 className="fa-section-title">Anotações Internas</h2>
                                <span className="fa-section-badge">Não visível para o cliente</span>
                            </div>
                            <div className="fa-field">
                                <textarea name="internalNotes" className="fa-textarea" rows={4}
                                    placeholder="Observações internas, lembretes, informações confidenciais sobre o caso..."
                                    value={formData.internalNotes} onChange={handleChange} />
                            </div>
                        </section>

                        {/* ── Anexos e Documentos ── */}
                        <section className="fa-card fa-docs-section">
                            <div className="fa-section-header">
                                <span className="fa-section-icon fa-section-icon--teal">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                                    </svg>
                                </span>
                                <h2 className="fa-section-title">Anexos e Documentos</h2>
                                <span className="fa-section-desc">Adicione imagens, laudos e outros arquivos</span>
                            </div>

                            {/* Drop zone */}
                            {!pendingDocFile && (
                                <div
                                    className="fa-docs-dropzone"
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={handleDocDrop}
                                    onClick={() => docInputRef.current?.click()}
                                >
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
                                        <polyline points="16 16 12 12 8 16" />
                                        <line x1="12" y1="12" x2="12" y2="21" />
                                        <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                                    </svg>
                                    <p className="fa-docs-dropzone-title">Arraste arquivos aqui ou clique para selecionar</p>
                                    <p className="fa-docs-dropzone-hint">Suporta: JPG, PNG, PDF (máx. 10MB por arquivo)</p>
                                    <button type="button" className="fa-docs-select-btn" onClick={(e) => { e.stopPropagation(); docInputRef.current?.click(); }}>
                                        Selecionar Arquivos
                                    </button>
                                    <input
                                        ref={docInputRef}
                                        type="file"
                                        accept=".jpg,.jpeg,.png,.webp,.pdf"
                                        style={{ display: 'none' }}
                                        onChange={(e) => handleDocFileSelect(e.target.files?.[0])}
                                    />
                                </div>
                            )}

                            {/* Pending file confirm form */}
                            {pendingDocFile && (
                                <div className="fa-docs-pending">
                                    <div className="fa-docs-pending-info">
                                        {pendingDocFile.previewUrl ? (
                                            <img src={pendingDocFile.previewUrl} alt="preview" className="fa-docs-preview-img" />
                                        ) : (
                                            <div className="fa-docs-file-icon fa-docs-file-icon--pdf">
                                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                    <polyline points="14 2 14 8 20 8" />
                                                </svg>
                                            </div>
                                        )}
                                        <span className="fa-docs-pending-name">{pendingDocFile.fileName}</span>
                                    </div>
                                    <div className="fa-field" style={{ margin: '12px 0 0' }}>
                                        <label className="fa-label">Título do documento *</label>
                                        <input
                                            type="text"
                                            className="fa-input"
                                            placeholder="Ex.: Laudo Raio-X Tórax"
                                            value={pendingDocTitle}
                                            onChange={(e) => setPendingDocTitle(e.target.value)}
                                        />
                                    </div>
                                    <label className="fa-docs-visibility-toggle">
                                        <input
                                            type="checkbox"
                                            className="fa-checkbox"
                                            checked={pendingDocVisibleToTutor}
                                            onChange={(e) => setPendingDocVisibleToTutor(e.target.checked)}
                                        />
                                        Visível para o tutor
                                    </label>
                                    {uploadProgress !== null && (
                                        <div className="fa-docs-progress">
                                            <div className="fa-docs-progress-bar" style={{ width: `${uploadProgress}%` }} />
                                        </div>
                                    )}
                                    <div className="fa-docs-pending-actions">
                                        <button type="button" className="fa-btn-med-cancel" onClick={() => { setPendingDocFile(null); setPendingDocTitle(''); setPendingDocVisibleToTutor(true); }} disabled={savingDoc}>
                                            Cancelar
                                        </button>
                                        <button type="button" className="fa-docs-confirm-btn" onClick={handleDocConfirm} disabled={savingDoc}>
                                            {savingDoc ? `Enviando${uploadProgress !== null ? ` ${uploadProgress}%` : '...'}` : 'Confirmar'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Document list */}
                            {documents.length > 0 && (
                                <div className="fa-docs-list">
                                    {documents.map((doc, i) => {
                                        const iconType = getDocIcon(doc);
                                        return (
                                            <div key={doc.id || i} className="fa-docs-item">
                                                <div className={`fa-docs-file-icon fa-docs-file-icon--${iconType}`}>
                                                    {iconType === 'img' ? (
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                                            <circle cx="8.5" cy="8.5" r="1.5" />
                                                            <polyline points="21 15 16 10 5 21" />
                                                        </svg>
                                                    ) : iconType === 'pdf' ? (
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                            <polyline points="14 2 14 8 20 8" />
                                                        </svg>
                                                    ) : (
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                            <polyline points="14 2 14 8 20 8" />
                                                            <line x1="16" y1="13" x2="8" y2="13" />
                                                            <line x1="16" y1="17" x2="8" y2="17" />
                                                        </svg>
                                                    )}
                                                </div>
                                                <div className="fa-docs-item-info">
                                                    <span className="fa-docs-item-title">{doc.title || doc.fileName}</span>
                                                    <span className="fa-docs-item-meta">{doc.fileName}</span>
                                                </div>
                                                <span className={`fa-docs-visibility-badge fa-docs-visibility-badge--${doc.visibleToTutor !== false ? 'visible' : 'hidden'}`}>
                                                    {doc.visibleToTutor !== false ? 'Visível ao tutor' : 'Interno'}
                                                </span>
                                                <div className="fa-docs-item-actions">
                                                    {doc.fileUrl && (
                                                        <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" className="fa-docs-action-btn fa-docs-action-btn--view" title="Visualizar">
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                                <circle cx="12" cy="12" r="3" />
                                                            </svg>
                                                        </a>
                                                    )}
                                                    <button type="button" className="fa-docs-action-btn fa-docs-action-btn--delete" title="Remover" onClick={() => handleDocDelete(doc, i)}>
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <polyline points="3 6 5 6 21 6" />
                                                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                            <path d="M10 11v6" /><path d="M14 11v6" />
                                                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </section>

                        {/* ── Actions ── */}
                        <div className="fa-form-actions">
                            <button className="fa-btn fa-btn--cancel" onClick={handleCancel} disabled={submitting}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <polyline points="15 18 9 12 15 6" />
                                </svg>
                                Cancelar
                            </button>
                            <div className="fa-form-actions-right">
                                <button className="fa-btn fa-btn--draft" onClick={handleSaveDraft} disabled={submitting}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                                        <polyline points="17 21 17 13 7 13 7 21" />
                                        <polyline points="7 3 7 8 15 8" />
                                    </svg>
                                    Salvar
                                </button>
                                <button className="fa-btn fa-btn--confirm" onClick={handleFinalize} disabled={submitting}>
                                    {submitting ? (
                                        <><div className="fa-btn-spinner" />Finalizando...</>
                                    ) : (
                                        <>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                            Finalizar Atendimento
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                    </main>
                </div>

                {showDetailsModal && (
                    <ScheduleDetailsModal scheduleId={scheduleId} onClose={() => setShowDetailsModal(false)} hideFinalizeButton />
                )}

                <ConfirmModal
                    isOpen={showEarlyFinalizeModal}
                    onClose={() => setShowEarlyFinalizeModal(false)}
                    onConfirm={() => doFinalize()}
                    type="info"
                    title="Finalizar antes do agendamento?"
                    message={<>O atendimento está agendado para <strong>{schedule ? formatDate(schedule.dateTime) : ''}</strong>. Tem certeza que deseja finalizar antes da data do agendamento?</>}
                    confirmText="Sim, finalizar"
                    cancelText="Cancelar"
                />
            </div>
        );
    }

    /* ══════════════════════════════════════════════════════
       OVERVIEW MODE
    ══════════════════════════════════════════════════════ */
    const d = savedData;

    return (
        <div className="fa-page">
            <header className="fa-header">
                <button className="fa-back-btn" onClick={handleCancel} aria-label="Voltar">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                    Voltar
                </button>
                <div className="fa-header-center">
                    <h1 className="fa-page-title">
                        Resumo do Atendimento
                        {isFinalized ? <span className="fa-finalized-badge">Finalizado</span> : <span className="indicator-badge badge-draft" style={{ marginLeft: '10px' }}>Rascunho</span>}
                    </h1>
                    <span className="fa-appointment-date">
                        {formatDate(schedule.dateTime)} · {formatTime(schedule.dateTime)}
                    </span>
                </div>
                {!isFinalized ? (
                    <button className="fa-btn--edit-header" onClick={handleEdit}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                        Editar
                    </button>
                ) : (
                    <div className="fa-header-spacer" />
                )}
            </header>

            <div className="fa-body">
                {sidebar}

                <main className="fa-form-col">

                    {(d.weight || d.temperature || d.heartRate || d.respiratoryRate || d.generalCondition || d.hydrationLevel || d.physicalExamNotes) && (
                        <section className="fa-card fa-section-card">
                            <div className="fa-section-header">
                                <span className="fa-section-icon fa-section-icon--teal">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                                    </svg>
                                </span>
                                <h2 className="fa-section-title">Exame Clínico</h2>
                            </div>
                            <div className="fa-ov-grid">
                                {d.weight && <div className="fa-ov-item"><span className="fa-ov-label">Peso</span><span className="fa-ov-value">{d.weight} kg</span></div>}
                                {d.temperature && <div className="fa-ov-item"><span className="fa-ov-label">Temperatura</span><span className="fa-ov-value">{d.temperature} °C</span></div>}
                                {d.heartRate && <div className="fa-ov-item"><span className="fa-ov-label">Freq. Cardíaca</span><span className="fa-ov-value">{d.heartRate} bpm</span></div>}
                                {d.respiratoryRate && <div className="fa-ov-item"><span className="fa-ov-label">Freq. Respiratória</span><span className="fa-ov-value">{d.respiratoryRate} rpm</span></div>}
                                {d.generalCondition && <div className="fa-ov-item"><span className="fa-ov-label">Estado Geral</span><span className="fa-ov-value">{getLabelFor(GENERAL_CONDITION_OPTIONS, d.generalCondition)}</span></div>}
                                {d.hydrationLevel && <div className="fa-ov-item fa-ov-item--wide"><span className="fa-ov-label">Hidratação</span><span className="fa-ov-value">{getLabelFor(HYDRATION_OPTIONS, d.hydrationLevel)}</span></div>}
                                {d.physicalExamNotes && <div className="fa-ov-item fa-ov-item--wide"><span className="fa-ov-label">Observações</span><span className="fa-ov-value">{d.physicalExamNotes}</span></div>}
                            </div>
                        </section>
                    )}

                    {(d.chiefComplaint || d.historyAndObservations) && (
                        <section className="fa-card fa-section-card">
                            <div className="fa-section-header">
                                <span className="fa-section-icon fa-section-icon--blue">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                    </svg>
                                </span>
                                <h2 className="fa-section-title">Anamnese</h2>
                            </div>
                            {d.chiefComplaint && (
                                <div className="fa-ov-text-block">
                                    <span className="fa-ov-label">Queixa Principal / Motivo da Consulta</span>
                                    <p className="fa-ov-text">{d.chiefComplaint}</p>
                                </div>
                            )}
                            {d.historyAndObservations && (
                                <div className="fa-ov-text-block">
                                    <span className="fa-ov-label">Histórico e Observações</span>
                                    <p className="fa-ov-text">{d.historyAndObservations}</p>
                                </div>
                            )}
                        </section>
                    )}

                    {(d.clinicalDiagnosis || d.requestedExams.length > 0 || d.otherExams) && (
                        <section className="fa-card fa-section-card">
                            <div className="fa-section-header">
                                <span className="fa-section-icon fa-section-icon--purple">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                                    </svg>
                                </span>
                                <h2 className="fa-section-title">Diagnóstico e Avaliação</h2>
                            </div>
                            {d.clinicalDiagnosis && (
                                <div className="fa-ov-text-block">
                                    <span className="fa-ov-label">Diagnóstico Clínico</span>
                                    <p className="fa-ov-text">{d.clinicalDiagnosis}</p>
                                </div>
                            )}
                            {d.requestedExams.length > 0 && (
                                <div className="fa-ov-text-block">
                                    <span className="fa-ov-label">Exames Solicitados</span>
                                    <div className="fa-ov-tags">
                                        {d.requestedExams.map((id) => (
                                            <span key={id} className="fa-ov-tag">
                                                {EXAM_OPTIONS.find((e) => e.id === id)?.label || id}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {d.otherExams && (
                                <div className="fa-ov-text-block">
                                    <span className="fa-ov-label">Outros Exames</span>
                                    <p className="fa-ov-text">{d.otherExams}</p>
                                </div>
                            )}
                        </section>
                    )}

                    {true && (
                        <section className="fa-card fa-section-card">
                            <div className="fa-section-header">
                                <span className="fa-section-icon fa-section-icon--orange">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                                    </svg>
                                </span>
                                <h2 className="fa-section-title">Tratamento e Prescrições</h2>
                                {!showMedForm && (
                                    <button type="button" className="fa-btn-add-med" onClick={openAddMedForm}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                            <line x1="12" y1="5" x2="12" y2="19" />
                                            <line x1="5" y1="12" x2="19" y2="12" />
                                        </svg>
                                        Adicionar Medicamento
                                    </button>
                                )}
                            </div>
                            {medications.length > 0 && (
                                <div className="fa-med-list">
                                    {medications.map((med, i) => (
                                        <div key={med.id || i} className="fa-med-list-item">
                                            <div className="fa-med-list-item-header">
                                                <div className="fa-med-list-item-info">
                                                    <p className="fa-med-list-item-name">{med.name}</p>
                                                    <div className="fa-med-list-item-details">
                                                        {med.dosage && <span>{med.dosage}</span>}
                                                        {med.frequencyTimes && med.frequencyType && (
                                                            <span>
                                                                {FREQUENCY_TIMES_OPTIONS.find((o) => o.value === med.frequencyTimes)?.label || med.frequencyTimes}{' '}
                                                                {FREQUENCY_TYPE_OPTIONS.find((o) => o.value === med.frequencyType)?.label || med.frequencyType}
                                                            </span>
                                                        )}
                                                        {med.durationTimes && med.durationType && (
                                                            <span>{med.durationTimes} {DURATION_TYPE_OPTIONS.find((o) => o.value === med.durationType)?.label || med.durationType}</span>
                                                        )}
                                                        {med.startDate && <span>{dayjs(med.startDate).format('DD/MM/YYYY')}{med.endDate ? ` → ${dayjs(med.endDate).format('DD/MM/YYYY')}` : ''}</span>}
                                                    </div>
                                                    {med.notes && <p className="fa-med-list-item-notes">{med.notes}</p>}
                                                </div>
                                                <div className="fa-med-list-item-actions">
                                                    <button type="button" className="fa-med-action-btn fa-med-action-btn--edit"
                                                        onClick={() => handleEditMed(i)} aria-label="Editar">
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                        </svg>
                                                    </button>
                                                    <button type="button" className="fa-med-action-btn fa-med-action-btn--delete"
                                                        onClick={() => handleDeleteMed(i)} aria-label="Remover">
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                            <polyline points="3 6 5 6 21 6" />
                                                            <path d="M19 6l-1 14H6L5 6" />
                                                            <path d="M10 11v6M14 11v6" />
                                                            <path d="M9 6V4h6v2" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {medications.length === 0 && !showMedForm && (
                                <p className="fa-details-hint" style={{ marginTop: '12px' }}>Nenhum medicamento prescrito.</p>
                            )}
                            {showMedForm && (
                                <div className="fa-med-form-panel">
                                    <h4 className="fa-med-form-title">{editingMedIndex !== null ? 'Editar Medicamento' : 'Novo Medicamento'}</h4>
                                    <div className="fa-grid-2">
                                        <div className="fa-field">
                                            <label className="fa-label">Nome do Medicamento *</label>
                                            <input type="text" className="fa-input" placeholder="Ex.: Amoxicilina" required
                                                value={medForm.name} onChange={(e) => handleMedFormChange('name', e.target.value)} />
                                        </div>
                                        <div className="fa-field">
                                            <label className="fa-label">Dosagem *</label>
                                            <input type="text" className="fa-input" placeholder="Ex.: 250mg ou 5mg/kg" required
                                                value={medForm.dosage} onChange={(e) => handleMedFormChange('dosage', e.target.value)} />
                                        </div>
                                    </div>
                                    <div className="fa-field">
                                        <label className="fa-label">Frequência *</label>
                                        <div className="fa-med-row">
                                            <select className="fa-select" value={medForm.frequencyTimes} required
                                                onChange={(e) => handleMedFormChange('frequencyTimes', e.target.value)}>
                                                <option value="">Selecione...</option>
                                                {FREQUENCY_TIMES_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                            </select>
                                            <select className="fa-select" value={medForm.frequencyType} required
                                                onChange={(e) => handleMedFormChange('frequencyType', e.target.value)}>
                                                <option value="">Selecione...</option>
                                                {FREQUENCY_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="fa-grid-2">
                                        <div className="fa-field">
                                            <label className="fa-label">Data de Início *</label>
                                            <input type="date" className="fa-input" required
                                                value={medForm.startDate} onChange={(e) => handleMedFormChange('startDate', e.target.value)} />
                                        </div>
                                        <div className="fa-field">
                                            <label className="fa-label">Duração <span style={{ color: 'red' }}>*</span></label>
                                            <div className="fa-med-row">
                                                <input type="number" className="fa-input" placeholder="Ex.: 7" min="1" required
                                                    value={medForm.durationTimes} onChange={(e) => handleMedFormChange('durationTimes', e.target.value)} />
                                                <select className="fa-select" value={medForm.durationType} required
                                                    onChange={(e) => handleMedFormChange('durationType', e.target.value)}>
                                                    <option value="">Selecione...</option>
                                                    {DURATION_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="fa-field">
                                        <label className="fa-label">Data de Término (calculada)</label>
                                        <input type="date" className="fa-input fa-input--readonly" value={medForm.endDate} readOnly />
                                    </div>
                                    <div className="fa-field">
                                        <label className="fa-label">Notas / Instruções de Uso</label>
                                        <textarea className="fa-textarea" rows={2} placeholder="Ex.: Administrar após alimentação"
                                            value={medForm.notes} onChange={(e) => handleMedFormChange('notes', e.target.value)} />
                                    </div>
                                    {/* <div className="fa-med-reminder-row">
                    <label className="fa-checkbox-label">
                      <input type="checkbox" className="fa-checkbox"
                        checked={medForm.reminder} onChange={(e) => handleMedFormChange('reminder', e.target.checked)} />
                      Ativar lembrete
                    </label>
                  </div> */}
                                    <div className="fa-med-form-actions">
                                        <button type="button" className="fa-btn-med-cancel" onClick={closeMedForm}>Cancelar</button>
                                        <button type="button" className="fa-btn-med-save" onClick={handleSaveMed} disabled={savingMed}>
                                            {savingMed ? 'Salvando...' : editingMedIndex !== null ? 'Salvar Alterações' : 'Adicionar'}
                                        </button>
                                    </div>
                                </div>
                            )}
                            {d.generalGuidelines && (
                                <div className="fa-ov-text-block">
                                    <span className="fa-ov-label">Orientações Gerais ao Tutor</span>
                                    <p className="fa-ov-text">{d.generalGuidelines}</p>
                                </div>
                            )}
                        </section>
                    )}

                    {(d.needsReturn || d.returnDeadline || d.returnNotes) && (
                        <section className="fa-card fa-section-card">
                            <div className="fa-section-header">
                                <span className="fa-section-icon fa-section-icon--green">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                        <line x1="16" y1="2" x2="16" y2="6" />
                                        <line x1="8" y1="2" x2="8" y2="6" />
                                        <line x1="3" y1="10" x2="21" y2="10" />
                                    </svg>
                                </span>
                                <h2 className="fa-section-title">Retorno e Acompanhamento</h2>
                            </div>
                            <div className="fa-ov-grid">
                                {d.needsReturn && <div className="fa-ov-item"><span className="fa-ov-label">Necessita Retorno</span><span className="fa-ov-value">{getLabelFor(NEEDS_RETURN_OPTIONS, d.needsReturn)}</span></div>}
                                {d.returnDeadline && <div className="fa-ov-item"><span className="fa-ov-label">Prazo para Retorno</span><span className="fa-ov-value">{d.returnDeadline}</span></div>}
                                {d.returnPriority && <div className="fa-ov-item"><span className="fa-ov-label">Prioridade</span><span className="fa-ov-value">{getLabelFor(PRIORITY_OPTIONS, d.returnPriority)}</span></div>}
                            </div>
                            {d.returnNotes && (
                                <div className="fa-ov-text-block">
                                    <span className="fa-ov-label">Observações para Próxima Consulta</span>
                                    <p className="fa-ov-text">{d.returnNotes}</p>
                                </div>
                            )}
                        </section>
                    )}

                    {d.internalNotes && (
                        <section className="fa-card fa-section-card">
                            <div className="fa-section-header">
                                <span className="fa-section-icon fa-section-icon--gray">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                        <line x1="3" y1="9" x2="21" y2="9" />
                                        <line x1="9" y1="21" x2="9" y2="9" />
                                    </svg>
                                </span>
                                <h2 className="fa-section-title">Anotações Internas</h2>
                                <span className="fa-section-badge">Não visível para o cliente</span>
                            </div>
                            <p className="fa-ov-text fa-ov-text--internal">{d.internalNotes}</p>
                        </section>
                    )}

                    {/* ── Anexos e Documentos (overview) ── */}
                    <section className="fa-card fa-docs-section">
                        <div className="fa-section-header">
                            <span className="fa-section-icon fa-section-icon--teal">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                                </svg>
                            </span>
                            <h2 className="fa-section-title">Anexos e Documentos</h2>
                            {!pendingDocFile && (
                                <button type="button" className="fa-btn-add-med" onClick={() => docInputRef.current?.click()}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <line x1="12" y1="5" x2="12" y2="19" />
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                    Adicionar Arquivo
                                </button>
                            )}
                            <input
                                ref={docInputRef}
                                type="file"
                                accept=".jpg,.jpeg,.png,.webp,.pdf"
                                style={{ display: 'none' }}
                                onChange={(e) => handleDocFileSelect(e.target.files?.[0])}
                            />
                        </div>

                        {/* Pending file confirm form */}
                        {pendingDocFile && (
                            <div className="fa-docs-pending">
                                <div className="fa-docs-pending-info">
                                    {pendingDocFile.previewUrl ? (
                                        <img src={pendingDocFile.previewUrl} alt="preview" className="fa-docs-preview-img" />
                                    ) : (
                                        <div className="fa-docs-file-icon fa-docs-file-icon--pdf">
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" />
                                            </svg>
                                        </div>
                                    )}
                                    <span className="fa-docs-pending-name">{pendingDocFile.fileName}</span>
                                </div>
                                <div className="fa-field" style={{ margin: '12px 0 0' }}>
                                    <label className="fa-label">Título do documento *</label>
                                    <input
                                        type="text"
                                        className="fa-input"
                                        placeholder="Ex.: Laudo Raio-X Tórax"
                                        value={pendingDocTitle}
                                        onChange={(e) => setPendingDocTitle(e.target.value)}
                                    />
                                </div>
                                <label className="fa-docs-visibility-toggle">
                                    <input
                                        type="checkbox"
                                        className="fa-checkbox"
                                        checked={pendingDocVisibleToTutor}
                                        onChange={(e) => setPendingDocVisibleToTutor(e.target.checked)}
                                    />
                                    Visível para o tutor
                                </label>
                                {uploadProgress !== null && (
                                    <div className="fa-docs-progress">
                                        <div className="fa-docs-progress-bar" style={{ width: `${uploadProgress}%` }} />
                                    </div>
                                )}
                                <div className="fa-docs-pending-actions">
                                    <button type="button" className="fa-btn-med-cancel" onClick={() => { setPendingDocFile(null); setPendingDocTitle(''); setPendingDocVisibleToTutor(true); }} disabled={savingDoc}>
                                        Cancelar
                                    </button>
                                    <button type="button" className="fa-docs-confirm-btn" onClick={handleDocConfirm} disabled={savingDoc}>
                                        {savingDoc ? `Enviando${uploadProgress !== null ? ` ${uploadProgress}%` : '...'}` : 'Confirmar'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Document list */}
                        {documents.length > 0 && (
                            <div className="fa-docs-list">
                                {documents.map((doc, i) => {
                                    const iconType = getDocIcon(doc);
                                    return (
                                        <div key={doc.id || i} className="fa-docs-item">
                                            <div className={`fa-docs-file-icon fa-docs-file-icon--${iconType}`}>
                                                {iconType === 'img' ? (
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                                        <circle cx="8.5" cy="8.5" r="1.5" />
                                                        <polyline points="21 15 16 10 5 21" />
                                                    </svg>
                                                ) : iconType === 'pdf' ? (
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                        <polyline points="14 2 14 8 20 8" />
                                                    </svg>
                                                ) : (
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                        <polyline points="14 2 14 8 20 8" />
                                                        <line x1="16" y1="13" x2="8" y2="13" />
                                                        <line x1="16" y1="17" x2="8" y2="17" />
                                                    </svg>
                                                )}
                                            </div>
                                            <div className="fa-docs-item-info">
                                                <span className="fa-docs-item-title">{doc.title || doc.fileName}</span>
                                                <span className="fa-docs-item-meta">{doc.fileName}</span>
                                            </div>
                                            <span className={`fa-docs-visibility-badge fa-docs-visibility-badge--${doc.visibleToTutor !== false ? 'visible' : 'hidden'}`}>
                                                {doc.visibleToTutor !== false ? 'Visível ao tutor' : 'Interno'}
                                            </span>
                                            <div className="fa-docs-item-actions">
                                                {doc.fileUrl && (
                                                    <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" className="fa-docs-action-btn fa-docs-action-btn--view" title="Visualizar">
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                            <circle cx="12" cy="12" r="3" />
                                                        </svg>
                                                    </a>
                                                )}
                                                <button type="button" className="fa-docs-action-btn fa-docs-action-btn--delete" title="Remover" onClick={() => handleDocDelete(doc, i)}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points="3 6 5 6 21 6" />
                                                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                        <path d="M10 11v6" /><path d="M14 11v6" />
                                                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {documents.length === 0 && !pendingDocFile && (
                            <p className="fa-details-hint" style={{ marginTop: '12px' }}>Nenhum documento adicionado.</p>
                        )}
                    </section>

                    <div className="fa-form-actions">
                        <button className="fa-btn fa-btn--cancel" onClick={handleCancel}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                            Voltar
                        </button>
                        {!isFinalized && (
                            <div className="fa-form-actions-right">
                                <button className="fa-btn fa-btn--draft" onClick={handleEdit}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                    Editar
                                </button>
                                <button className="fa-btn fa-btn--confirm" onClick={handleFinalize} disabled={submitting}>
                                    {submitting ? (
                                        <><div className="fa-btn-spinner" />Finalizando...</>
                                    ) : (
                                        <>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                            Finalizar Atendimento
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>

                </main>
            </div>

            {showDetailsModal && (
                <ScheduleDetailsModal scheduleId={scheduleId} onClose={() => setShowDetailsModal(false)} hideFinalizeButton />
            )}
        </div>
    );
}

export default FinalizeAppointment;
